import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { Logo } from "@/components/Shared";

const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  const links = [
    { label: t.nav.solution, to: "/solution", testid: "nav-solution-link" },
    { label: t.nav.who, to: "/pour-qui", testid: "nav-who-link" },
    { label: t.nav.features, to: "/fonctionnalites", testid: "nav-features-link" },
    { label: t.nav.resources, to: "/ressources", testid: "nav-resources-link" },
    { label: t.nav.pricing, to: "/tarifs", testid: "nav-pricing-link" },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || open ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" aria-label="Orbit">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-menu">
          {links.map((l) => (
            <Link
              key={l.testid}
              to={l.to}
              data-testid={l.testid}
              className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-slate-900"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <div
            className="flex items-center rounded-full border border-slate-200 bg-white p-0.5"
            data-testid="lang-toggle"
          >
            {["fr", "en"].map((code) => (
              <button
                key={code}
                data-testid={`lang-toggle-${code}`}
                onClick={() => setLang(code)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors duration-200 ${
                  lang === code ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-login-btn"
            onClick={() => toast(t.hero.loginToast)}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-slate-400 hover:bg-slate-50"
          >
            {t.nav.login}
          </button>
          <Link
            to="/demo"
            data-testid="nav-demo-btn"
            className="group flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-700"
          >
            {t.nav.demo}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 lg:hidden"
          aria-label={t.nav.menu}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-100 bg-white px-5 pb-6 pt-3 lg:hidden" data-testid="mobile-menu">
          {links.map((l) => (
            <Link
              key={l.testid}
              to={l.to}
              data-testid={`mobile-${l.testid}`}
              className="block border-b border-slate-50 py-3 text-base font-medium text-slate-700"
            >
              {l.label}
            </Link>
          ))}
          <div className="mt-4 flex items-center gap-3">
            <div className="flex items-center rounded-full border border-slate-200 p-0.5">
              {["fr", "en"].map((code) => (
                <button
                  key={code}
                  data-testid={`mobile-lang-toggle-${code}`}
                  onClick={() => setLang(code)}
                  className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase ${
                    lang === code ? "bg-slate-900 text-white" : "text-slate-500"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
            <Link
              to="/demo"
              data-testid="mobile-nav-demo-btn"
              className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-center text-sm font-semibold text-white"
            >
              {t.nav.demo}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
